import React from 'react';

import {
  Box,
  Flex,
  Text,
  Badge,
  Link,
  Divider,
  HStack,
  VStack,
  Image,
  Tooltip,
} from '@chakra-ui/react';

import { ExternalLinkIcon, InfoIcon } from '@chakra-ui/icons';

import { getMarketplaceLogo } from 'components/marketplaceLogo';

import { useColors } from './colors';

const ListingHeader = ({ listing }) => {
  const { bgColor, borderColor, textColor, headingColor } = useColors();

  // Status badge configuration
  const getStatusConfig = (status) => {
    switch (status) {
      case 'synced':
        return { colorScheme: 'blue', label: 'SYNCED' };
      case 'not synced':
        return { colorScheme: 'yellow', label: 'NOT SYNCED' };
      case 'error':
        return { colorScheme: 'red', label: 'ERROR' };
      default:
        return { colorScheme: 'gray', label: 'UNKNOWN' };
    }
  };

  const statusConfig = getStatusConfig(listing.status);
  const isAmazon = listing.marketplace?.name?.toLowerCase() === 'amazon';

  return (
    <Box
      bg={bgColor}
      borderRadius="xl"
      p={6}
      border="1px solid"
      borderColor={borderColor}
      mb={6}
    >
      <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
        <Flex flexDirection="column" justify="start" wrap="wrap" gap={1}>
          <Link
            href={
              listing?.marketplace?.name?.toLowerCase() === 'ebay'
                ? `https://www.ebay.com/itm/${listing.externalPlatformId}`
                : listing?.marketplace?.name?.toLowerCase() === 'amazon'
                  ? `https://www.amazon.com/dp/${listing.externalPlatformId}`
                  : '#'
            }
            isExternal
            textDecoration="none"
          >
            <HStack spacing={2}>
              <Text
                fontSize="2xl"
                fontWeight="bold"
                color={headingColor}
                display="flex"
                alignItems="center"
                _hover={{ textDecoration: 'underline' }}
              >
                {isAmazon ? 'ASIN' : 'ITEM ID'} / {listing.externalPlatformId}
              </Text>
              <ExternalLinkIcon color={headingColor} boxSize={5} />
              <Badge
                colorScheme={statusConfig.colorScheme}
                variant="subtle"
                fontSize="lg"
                fontWeight="bold"
                px={6}
                py={1}
                borderRadius="md"
                _hover={{ textDecoration: 'none' }}
              >
                {statusConfig.label}
              </Badge>
              {listing.statusMessage && (
                <Tooltip
                  label={listing.statusMessage}
                  placement="top"
                  hasArrow
                  bg="gray.700"
                  color="white"
                  fontSize="sm"
                  px={3}
                  py={2}
                  borderRadius="md"
                >
                  <InfoIcon
                    color={headingColor}
                    boxSize={4}
                    cursor="pointer"
                    _hover={{ opacity: 0.7 }}
                  />
                </Tooltip>
              )}
            </HStack>
          </Link>
          <VStack align="start" spacing={1} mt={4} color={textColor} fontSize="sm">
            <Text>
              <Text as="span" fontWeight="semibold">
                Last Synced:
              </Text>{' '}
              {listing.lastSyncedAt || 'Never'}
            </Text>
            <Text>
              <Text as="span" fontWeight="semibold">
                Created On:
              </Text>{' '}
              {listing.marketplaceDetails?.createdOnDate || listing.createdAt}
            </Text>
          </VStack>
        </Flex>

        <Flex align="center" justify="flex-end">
          <HStack spacing={4} align="center">
            <Text fontSize="2xl" fontWeight="semibold" color={textColor}>
              {listing.supplier?.altName ||
                listing.supplier?.name ||
                'Supplier Not Selected'}
            </Text>

            <Divider orientation="vertical" height="90px" borderColor="gray.300" />

            <Box>
              <Image
                src={getMarketplaceLogo(listing.marketplace?.name)}
                alt={`${listing.marketplace?.name} logo`}
                height="60px"
                maxW="120px"
                objectFit="contain"
                marginRight={4}
              />
            </Box>
          </HStack>
        </Flex>
      </Flex>
    </Box>
  );
};

export default ListingHeader;
